/* ──────────────────────────────────────────────────────────
   EmployeeCostSetup – editable grid with Pen ⇆ Save toggle
   ────────────────────────────────────────────────────────── */
   import React, { useState, useEffect } from "react";
   import PropTypes from "prop-types";
   
   import Tooltip       from "@mui/material/Tooltip";
   import IconButton    from "@mui/material/IconButton";
   import ModeIcon      from "@mui/icons-material/ModeOutlined";        // ✏️
   import SaveIcon      from "@mui/icons-material/SaveOutlined";        // 💾
   import AddIcon       from "@mui/icons-material/AddOutlined";         // ➕
   import RefreshIcon   from "@mui/icons-material/RefreshOutlined";     // 🗘
   import CloseIcon     from "@mui/icons-material/CloseOutlined";       // ×
   
   /* ── minimal cell component ── */
   function Cell({ value, onChange, readOnly=false, align="left" }) {
     return (
       <td className={`p-1 border text-sm text-${align}`}>
         {readOnly ? (
           value
         ) : (
           <input
             className="w-full bg-white border px-1 text-xs"
             value={value ?? ""}
             onChange={e => onChange(e.target.value)}
           />
         )}
       </td>
     );
   }
   Cell.propTypes = {
     value:    PropTypes.any,
     onChange: PropTypes.func,
     readOnly: PropTypes.bool,
     align:    PropTypes.oneOf(["left","right","center"]),
   };
   
   /* ── main component ── */
   export default function EmployeeCostSetup({
     initialRows = [],
     onSave      = undefined,
     onClose     = undefined,
   }) {
     const emptyRow = () => ({
       id: Date.now(),
       employee:"", effStart:"", effEnd:"", hrsDay:"",
       otEndAM:"", otStartPM:"", rate:"", exp:"", ohPct:"", total:""
     });
   
     /* rows + editing state */
     const [rows, setRows]       = useState(initialRows.length ? initialRows : [emptyRow()]);
     const [isEditing, setEdit]  = useState(false);
   
     /* when parent passes a new initialRows, refresh grid */
     useEffect(() => {
       setRows(initialRows.length ? initialRows : [emptyRow()]);
     }, [initialRows]);
   
     /* helpers ----------------------------------------------------------- */
     const handleChange = (rowIdx, field, value) =>
       setRows(p => p.map((r,i)=> i===rowIdx ? { ...r, [field]: value } : r));
   
     const addRow = () => {
       setRows(p => [...p, emptyRow()]);
       setEdit(true);                   // auto-enter edit mode
     };
   
     const removeRow = idx =>
       setRows(p => p.filter((_,i)=> i!==idx));
   
     const refresh = () =>
       setRows(initialRows.length ? initialRows : [emptyRow()]);
   
     const cols = [
       ["employee",  "Employee",    "left"],
       ["effStart",  "EFF START",   "center"],
       ["effEnd",    "EFF END",     "center"],
       ["hrsDay",    "Hrs/Day",     "right"],
       ["otEndAM",   "OT End AM",   "center"],
       ["otStartPM", "OT Start PM", "center"],
       ["rate",      "RATE",        "right"],
       ["exp",       "EXP",         "right"],
       ["ohPct",     "% OH",        "right"],
       ["total",     "TOTAL",       "right"],
     ];
   
     /* ── render ── */
     return (
       <div className="h-full flex flex-col">
   
         {/* toolbar */}
         <div className="flex items-center gap-3 p-2 border-b bg-gray-50">
   
           {/* Edit ⇆ Save */}
           <Tooltip title={isEditing ? "Save changes" : "Edit"}>
             <IconButton size="small" onClick={() => {
               if (isEditing) {
                 onSave?.(rows);
                 setEdit(false);
               } else {
                 setEdit(true);
               }
             }}>
               {isEditing
                 ? <SaveIcon fontSize="small" className="text-blue-600" />
                 : <ModeIcon fontSize="small" className="text-gray-700" />}
             </IconButton>
           </Tooltip>
   
           {/* Add Row (always enabled) */}
           <Tooltip title="Add row">
             <IconButton size="small" onClick={addRow}>
               <AddIcon fontSize="small" />
             </IconButton>
           </Tooltip>
   
           {/* Refresh (only when not editing) */}
           <Tooltip title="Refresh list">
             <IconButton size="small" disabled={isEditing} onClick={refresh}>
               <RefreshIcon fontSize="small" />
             </IconButton>
           </Tooltip>
   
           {/* Close */}
           {onClose && (
             <Tooltip title="Close">
               <IconButton size="small" onClick={onClose}>
                 <CloseIcon fontSize="small" />
               </IconButton>
             </Tooltip>
           )}
         </div>
   
         {/* grid */}
         <div className="flex-1 overflow-auto">
           <table className="min-w-full border-collapse">
             <thead className="sticky top-0 bg-gray-100 text-xs">
               <tr>
                 {cols.map(([k, hdr]) => (
                   <th key={k} className="border px-1 py-1 whitespace-nowrap font-semibold">
                     {hdr}
                   </th>
                 ))}
                 <th className="border px-1 py-1" />
               </tr>
             </thead>
   
             <tbody>
               {rows.map((row, rowIdx) => (
                 <tr key={row.id || rowIdx} className="odd:bg-white even:bg-gray-50">
                   {cols.map(([k, , align]) => (
                     <Cell
                       key={k}
                       align={align}
                       readOnly={!isEditing}
                       value={row[k]}
                       onChange={val => handleChange(rowIdx, k, val)}
                     />
                   ))}
                   <td className="border px-1 text-center">
                     <Tooltip title="Delete row">
                       <span>
                         <IconButton size="small" disabled={!isEditing}
                                     onClick={() => removeRow(rowIdx)}>
                           <CloseIcon fontSize="inherit" className="text-red-500" />
                         </IconButton>
                       </span>
                     </Tooltip>
                   </td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
       </div>
     );
   }
   
   EmployeeCostSetup.propTypes = {
     initialRows: PropTypes.array,
     onSave:      PropTypes.func,
     onClose:     PropTypes.func,
   };
   